import { getHandicapStrokes } from './golf';

/**
 * Is this tournament format a MATCH — decided hole by hole, not by a total?
 *
 * The one client-side definition, mirroring `is_matchplay_format` in the
 * server's models.py. It matches the word rather than listing 'Singles
 * Matchplay' and 'Pairs Matchplay', so a future variant is included by default:
 * a format wrongly treated as matchplay hides a figure, one wrongly treated as
 * stroke prints a meaningless one.
 *
 * WHAT DEPENDS ON IT, and why. A match is not a card. It ends when it is won —
 * 4&3 means three holes were never played — and holes are conceded rather than
 * holed out. So the Stableford total and the score-to-par of a matchplay round
 * are arithmetic over a card that was never finished:
 *
 *   • Home's recent form drops the round rather than showing it (homeScores.js);
 *   • the scoring screen offers no My Stats tab for it, and neither the round
 *     list on My Performance nor the standalone stats screen will take it —
 *     a fairway-hit rate over fifteen holes of which two were conceded is not
 *     a measurement of anything.
 *
 * Advanced Foursomes/Greensomes matchplay is NOT covered: those tournaments are
 * created as 'Stableford' and carry their mode in the competition config, and
 * they score to a shared team card rather than to the individual rounds these
 * surfaces read.
 */
export const isMatchplayFormat = (format) =>
  typeof format === 'string' && format.toLowerCase().includes('matchplay');

// Hole-by-hole state of a match between two shared-ball sides, for the SCORING
// ENTRY card (TeamLiveScoring). The leaderboard's matches come computed from
// the server (services/advanced/matchplay.py) and nothing here replaces that —
// this exists because the entry screen has to work with no network, so it
// cannot ask the server what the last hole did to the match.
//
// It mirrors compute_match's loop deliberately and minimally:
//   • a hole counts only once BOTH sides have a gross score — a match is a
//     comparison, so one side playing ahead decides nothing;
//   • nett is gross minus the strokes THIS MATCH gives, which is not either
//     side's own team handicap: a match is played off the DIFFERENCE, and the
//     server hands each side its `match_shots_received` (0 for the lower side)
//     precisely so the client never has to work out who gives shots to whom;
//   • a concluded match still plays out, but the extra holes are dead and must
//     not move the score.
// Strokes come from the shared getHandicapStrokes — the same allocation every
// score in the app is written with — so a shot lands on the same holes here as
// on the board.
//
// `holes` must already be in PLAY order (starting hole first): a match is
// decided by holes remaining, so a shotgun start off the 10th reaches its last
// hole at the 9th green.
//
// Returns, from side A's point of view:
//   byHole  {hole: {result: 1 won | -1 lost | 0 halved, margin, isDead}}
//   margin  holes up (negative: down) after the last live hole
//   thru    holes both sides have completed
//   state   'in_progress' | 'won' | 'halved' (matches compute_match's states)
export const matchProgress = (holes, sideA, sideB) => {
  const byHole = {};
  let margin = 0;
  let thru = 0;
  let concluded = false;
  // A hole flagged `isExtra` is a play-off hole: the match was level after its
  // last hole and went back out. The first one won ends it, and a halved one
  // means they play another — so a level match with extra holes in play is
  // never "halved", however level it reads.
  const inPlayoff = (holes || []).some((h) => h.isExtra);

  (holes || []).forEach((h) => {
    const grossA = parseInt(sideA?.scores?.[h.hole_number], 10);
    const grossB = parseInt(sideB?.scores?.[h.hole_number], 10);
    if (Number.isNaN(grossA) || Number.isNaN(grossB)) return;

    const nettA = grossA - getHandicapStrokes(sideA?.shots || 0, h.strokeIndex);
    const nettB = grossB - getHandicapStrokes(sideB?.shots || 0, h.strokeIndex);
    const result = nettA < nettB ? 1 : (nettB < nettA ? -1 : 0);

    if (concluded) {
      byHole[h.hole_number] = { result, margin, isDead: true };
      return;
    }
    margin += result;
    thru += 1;
    byHole[h.hole_number] = { result, margin, isDead: false };
    // Decided: more holes up than are left to play.
    if (Math.abs(margin) > (holes.length - thru)) concluded = true;
  });

  let state = 'in_progress';
  if (concluded) state = 'won';
  else if (inPlayoff) state = 'in_progress';
  else if (thru === (holes || []).length && thru > 0) state = margin === 0 ? 'halved' : 'won';

  return { byHole, margin, thru, state, isConcluded: concluded, inPlayoff };
};

// The real course hole a play-off hole is played on. Mirrors the server's
// services/matchplay_playoff.course_hole_for — the scoring screen needs it to
// label the column and read the hole's par and stroke index, and must work
// with no network, so it cannot ask. Hole 19 is the play-off's start hole, and
// the sequence wraps at the 18th back to the 1st.
export const PLAYOFF_FIRST_HOLE = 19;

export const playoffCourseHole = (holeNumber, startHole) =>
  (((Number(startHole) || 1) - 1 + (Number(holeNumber) - PLAYOFF_FIRST_HOLE)) % 18) + 1;

// `count` play-off holes as scoring-card columns, each labelled with the course
// hole it is actually played on and carrying that hole's par and stroke index
// (`card` is the round's own hole card). The caller decides how many: the holes
// already played, plus the one they're walking to while the match is still
// live.
export const playoffHoles = (startHole, card, count) => {
  if (!startHole || count <= 0) return [];
  return Array.from({ length: count }, (_, i) => {
    const holeNumber = PLAYOFF_FIRST_HOLE + i;
    const courseHole = playoffCourseHole(holeNumber, startHole);
    const hole = (card || []).find((h) => h.hole_number === courseHole);
    return {
      hole_number: holeNumber,
      courseHole,
      par: hole?.par,
      strokeIndex: hole?.strokeIndex,
      isExtra: true,
    };
  });
};

// How many play-off holes BOTH sides have completed — a match is a comparison,
// so a hole one side has played alone isn't in play yet.
export const countPlayedExtraHoles = (scoresA, scoresB) => {
  let played = 0;
  while (scoresA?.[PLAYOFF_FIRST_HOLE + played] != null
         && scoresB?.[PLAYOFF_FIRST_HOLE + played] != null) played += 1;
  return played;
};

// A side's standing in its match, in the shorthand a card is kept in: "2 UP",
// "A/S" (the same all-square shorthand the match cards use), "2 DN".
export const matchStanding = (margin) => {
  if (!margin) return 'A/S';
  return margin > 0 ? `${margin} UP` : `${Math.abs(margin)} DN`;
};

// ── The legacy board's payload, as the scoring card needs it ───────────────
// The individual Singles/Pairs card can't work these out for itself: the shots
// a match gives are the difference from the LOWEST handicap in the match times
// the day's allowance, and a hole is won on a side's better ball. Both are
// computed by tournaments.py::get_matchplay_leaderboard, so the card takes them
// rather than re-deriving — the same reason the shared-ball card is handed
// `match_shots_received`. These two turn that payload into per-player answers.

// playerId -> { shots, side } for everyone in a match.
export const matchPlayersFrom = (match) => {
  const map = {};
  ['red', 'blue'].forEach((side) => {
    (match?.[`${side}_team`] || []).forEach((p) => {
      if (p && p.id != null) map[p.id] = { shots: p.match_shots, side };
    });
  });
  return map;
};

// One hole from one player's point of view: 1 won, -1 lost, 0 halved, null if
// the hole isn't decided yet (or the player isn't in this match).
// A half is `winner: null`; `'tie'` is tolerated because that is what the board
// sent before it was normalised, and a client running against a cached older
// payload must not read a half as a loss.
export const holeOutcomeFor = (match, matchPlayers, playerId, holeNumber) => {
  const mine = matchPlayers?.[playerId];
  if (!mine) return null;
  return holeOutcomeForSide(match, mine.side, holeNumber);
};

// The same hole from a SIDE's point of view. A pair is one ball in the match —
// the hole is won on their better ball — so the outcome belongs to the side and
// a player only inherits it (holeOutcomeFor is this, looked up by player).
export const holeOutcomeForSide = (match, side, holeNumber) => {
  const hole = (match?.hole_results || []).find((h) => h.hole_number === holeNumber);
  if (!hole || !side) return null;
  if (!hole.winner || hole.winner === 'tie') return 0;
  return hole.winner === side ? 1 : -1;
};

// Where the match stands, in holes, from one side's point of view — positive
// up, negative down. Read off the board's own running margin (`up_by` and
// `leader_color` on the last decided hole) rather than recounted here: the
// board is the only place a match result is decided, and a second count on the
// card is a second answer waiting to disagree with it.
// null when no hole has been decided yet — a match with nothing played is not
// "all square", it hasn't started.
export const matchMarginFor = (match, side) => {
  const results = match?.hole_results || [];
  const last = results[results.length - 1];
  if (!last) return null;
  if (!last.leader_color) return 0;
  return last.leader_color === side ? (Number(last.up_by) || 0) : -(Number(last.up_by) || 0);
};
